import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWorkspaceTabNavigation } from '../../context/WorkspaceTabsContext';
import gameService from '../../services/gameService';
import { ArrowLeftIcon } from '../../components/Icons';
import { normalizeCredentialFields } from '../../utils/credentialFields';

const cardStyle = {
  padding: '1.5rem',
  marginBottom: '1.5rem'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: '0.35rem'
};

const valueStyle = {
  fontSize: '0.95rem',
  color: 'var(--text-primary)',
  fontWeight: 600
};

const renderValue = (value) => value || '-';

const formatExternalLink = (value) => {
  const link = String(value || '').trim();
  if (!link) return '';
  return /^https?:\/\//i.test(link) ? link : `https://${link}`;
};

const formatWinIndicator = (value) => {
  if (value === 'positive') return 'Positive';
  if (value === 'negative') return 'Negative';
  return '-';
};

const getReportingWinIndicator = (provider, side) => (
  provider?.[`reporting_${side}_win_indicator`] || provider?.[`reporting_${side}_winlose_indicator`]
);

const MasterProviderView = () => {
  const navigate = useNavigate();
  const { closeCurrentTabAndNavigate } = useWorkspaceTabNavigation();
  const { id } = useParams();
  const { hasPermission, getDefaultRoute } = useAuth();
  const [loading, setLoading] = useState(true);
  const [masterProvider, setMasterProvider] = useState(null);

  const canView = hasPermission('master_providers', 'view');
  const canViewFinancial = hasPermission('master_providers', 'view_financial');
  const canViewReporting = hasPermission('master_providers', 'view_reporting');

  const fetchMasterProvider = useCallback(async () => {
    try {
      setLoading(true);
      const res = await gameService.getMasterProviderById(id);

      if (res.success) {
        setMasterProvider(res.data);
      } else {
        alert(res.message || 'Failed to fetch master provider details');
        navigate('/games/master-providers');
      }
    } catch (error) {
      alert(error.response?.data?.message || error.message || 'Failed to fetch master provider details');
      navigate('/games/master-providers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!canView) {
      navigate(getDefaultRoute(), { replace: true });
      return;
    }

    fetchMasterProvider();
  }, [canView, fetchMasterProvider, getDefaultRoute, navigate]);

  if (loading) {
    return <div className="page-container" style={{ padding: '2rem', textAlign: 'center' }}>Loading master provider details...</div>;
  }

  if (!masterProvider) {
    return <div className="page-container" style={{ padding: '2rem', textAlign: 'center' }}>Master provider not found.</div>;
  }

  const boCredentialFields = normalizeCredentialFields(masterProvider.bo_credential_keys);
  const apiCredentialFields = normalizeCredentialFields(masterProvider.api_credential_keys);

  return (
    <div className="master-provider-view-page" style={{ maxWidth: '1100px', margin: '0 auto', padding: '1rem' }}>
      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button className="btn" onClick={() => closeCurrentTabAndNavigate('/games/master-providers')} style={{ padding: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ArrowLeftIcon />
        </button>
      </div>

      <div className="card" style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-primary)' }}>{renderValue(masterProvider.provider_id?.name)}</h2>
            <div style={{ marginTop: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{renderValue(masterProvider.code)}</div>
            <div style={{ marginTop: '0.25rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Backend Code: {renderValue(masterProvider.backend_code)}</div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <span style={{ padding: '0.4rem 0.7rem', borderRadius: '9999px', fontSize: '0.78rem', fontWeight: 700, backgroundColor: masterProvider.is_active ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)', color: masterProvider.is_active ? 'var(--success)' : 'var(--error)' }}>
              {masterProvider.is_active ? 'Active' : 'Inactive'}
            </span>
            <span style={{ padding: '0.4rem 0.7rem', borderRadius: '9999px', fontSize: '0.78rem', fontWeight: 700, backgroundColor: 'rgba(var(--primary-rgb), 0.12)', color: masterProvider.type === 'Seamless' ? 'var(--accent)' : 'var(--secondary)' }}>
              {renderValue(masterProvider.type)}
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
          <div>
            <div style={labelStyle}>Category</div>
            <div style={valueStyle}>{renderValue(masterProvider.provider_id?.category?.name)}</div>
          </div>
          <div>
            <div style={labelStyle}>Sub Category</div>
            <div style={valueStyle}>{renderValue(masterProvider.provider_id?.sub_category?.name)}</div>
          </div>
          <div>
            <div style={labelStyle}>Source</div>
            <div style={valueStyle}>{renderValue(masterProvider.source?.name)}</div>
          </div>
          <div>
            <div style={labelStyle}>Shortcode</div>
            <div style={valueStyle}>{renderValue(masterProvider.shortcode)}</div>
          </div>
          <div>
            <div style={labelStyle}>Backend Code</div>
            <div style={valueStyle}>{renderValue(masterProvider.backend_code)}</div>
          </div>
          <div>
            <div style={labelStyle}>Currency</div>
            <div style={valueStyle}>{renderValue(masterProvider.currency?.currency_code || masterProvider.currency?.code)}</div>
          </div>
          <div>
            <div style={labelStyle}>Country</div>
            <div style={valueStyle}>{renderValue(masterProvider.country?.code)}</div>
          </div>
          <div>
            <div style={labelStyle}>Topup Options</div>
            <div style={valueStyle}>{masterProvider.topup_options ? 'Yes' : 'No'}</div>
          </div>
        </div>
      </div>

      <div className="card" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>Group Links</h3>
        {(masterProvider.group_links || []).length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {masterProvider.group_links.map((link, index) => (
              <a key={`${typeof link === 'string' ? link : link.url}-${index}`} href={typeof link === 'string' ? link : link.url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', wordBreak: 'break-all', textDecoration: 'none', padding: '0.75rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)' }}>
                <span style={{ display: 'block', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.2rem' }}>{typeof link === 'string' ? `Link ${index + 1}` : (link.name || `Link ${index + 1}`)}</span>
                <span>{typeof link === 'string' ? link : link.url}</span>
              </a>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--text-muted)' }}>No group links configured.</div>
        )}
      </div>

      {canViewFinancial && (
      <div className="card" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>Financial</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
          <div>
            <div style={labelStyle}>Costing Rate</div>
            <div style={valueStyle}>{masterProvider.costing_rates != null ? `${masterProvider.costing_rates}%` : '-'}</div>
          </div>
          <div>
            <div style={labelStyle}>Settlement Period</div>
            <div style={valueStyle}>{renderValue(masterProvider.settlement_period)}</div>
          </div>
          <div>
            <div style={labelStyle}>Carry Forward</div>
            <div style={valueStyle}>{masterProvider.is_carry_forward ? 'Yes' : 'No'}</div>
          </div>
        </div>
      </div>
      )}

      {canViewReporting && (
      <div className="card" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>Reporting</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem' }}>
          <div>
            <div style={labelStyle}>Provider Timezone</div>
            <div style={valueStyle}>{renderValue(masterProvider.reporting_game_provider_timezone)}</div>
          </div>
          <div>
            <div style={labelStyle}>BO Timezone</div>
            <div style={valueStyle}>{renderValue(masterProvider.reporting_bo_timezone)}</div>
          </div>
          <div>
            <div style={labelStyle}>Company Win Indicator</div>
            <div style={valueStyle}>{formatWinIndicator(getReportingWinIndicator(masterProvider, 'company'))}</div>
          </div>
          <div>
            <div style={labelStyle}>Player Win Indicator</div>
            <div style={valueStyle}>{formatWinIndicator(getReportingWinIndicator(masterProvider, 'player'))}</div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={labelStyle}>Remarks</div>
            <div style={{ ...valueStyle, whiteSpace: 'pre-wrap' }}>{renderValue(masterProvider.reporting_remarks)}</div>
          </div>
        </div>
      </div>
      )}

      <div className="card" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>Credential Setup</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div>
            <div style={labelStyle}>BO Credential Keys</div>
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Provider BO Link</div>
              {masterProvider.reporting_provider_bo_link ? (
                <a href={formatExternalLink(masterProvider.reporting_provider_bo_link)} target="_blank" rel="noreferrer" style={{ ...valueStyle, color: 'var(--primary)', wordBreak: 'break-all', textDecoration: 'none' }}>
                  {masterProvider.reporting_provider_bo_link}
                </a>
              ) : (
                <div style={valueStyle}>-</div>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {boCredentialFields.length ? boCredentialFields.map((field) => (
                <span key={field.key} style={{ padding: '0.3rem 0.65rem', borderRadius: '9999px', fontSize: '0.8rem', backgroundColor: 'var(--bg-soft)', color: 'var(--text-secondary)', border: '1px solid var(--border-light)' }}>
                  {field.key}{field.defaultValue ? ` (default: ${field.defaultValue})` : ''}
                </span>
              )) : <span style={{ color: 'var(--text-muted)' }}>No keys</span>}
            </div>
          </div>
          <div>
            <div style={labelStyle}>API Credential Keys</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {apiCredentialFields.length ? apiCredentialFields.map((field) => (
                <span key={field.key} style={{ padding: '0.3rem 0.65rem', borderRadius: '9999px', fontSize: '0.8rem', backgroundColor: 'var(--bg-soft)', color: 'var(--text-secondary)', border: '1px solid var(--border-light)' }}>
                  {field.key}{field.defaultValue ? ` (default: ${field.defaultValue})` : ''}
                </span>
              )) : <span style={{ color: 'var(--text-muted)' }}>No keys</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>Callback URLs</h3>
        {(masterProvider.callback_urls || []).length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {masterProvider.callback_urls.map((callback) => (
              <div key={`${callback.name}-${callback.path}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.85rem 1rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{callback.name}</div>
                <div style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{callback.path}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--text-muted)' }}>No callback URLs configured.</div>
        )}
      </div>

      <div className="card" style={cardStyle}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)' }}>Documents</h3>
        {(masterProvider.documents || []).length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {masterProvider.documents.map((doc) => (
              <div key={doc.object_key || doc.file_path || doc.file_name} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '1rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{doc.original_name}</div>
                  <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    {doc.label || 'No label'} · {doc.size ? `${(doc.size / 1024 / 1024).toFixed(doc.size >= 1024 * 1024 ? 2 : 1)} MB` : 'Unknown size'}
                  </div>
                </div>
                <a
                  className="btn btn-primary"
                  href={gameService.getMasterProviderDocumentUrl(doc)}
                  target="_blank"
                  rel="noreferrer"
                  download={doc.original_name}
                  style={{ textDecoration: 'none' }}
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: 'var(--text-muted)' }}>No documents available.</div>
        )}
      </div>
    </div>
  );
};

export default MasterProviderView;
